import React, { ReactElement, useEffect } from "react";
import { Icon, Navigation } from "@shopify/polaris";
import {
    CameraMajor,
    ChecklistMajor,
    NoteMajor,
    CircleTickMajor,
    NatureMajor,
    FolderMajor
} from "@shopify/polaris-icons";
import { SideBarData } from "../../types/SideBarData";
import {Link, useLocation, useHistory} from "react-router-dom";
import style from "./SideBar.module.css";
import arrow from "../../images/arrow.svg";
import MainFrameContextType from "../../types/MainFrameContextType";
import {MainFrameContext} from "../contexts/MainFrameContext";
import {useDispatch, useSelector} from "react-redux";
import { PortalState } from "../../types/PortalState";
import { AssetType } from "../../types/AssetType";
import SurveyorCompletionStatus from "../../types/SurveyorCompletionStatus";
import {userIsDocCollector} from "../../helpers/users";
import {clearDeleteData} from "../../store/actions/Asset";
import { useFlags } from "launchdarkly-react-client-sdk";

export default function SideBar(): ReactElement {
    const data: SideBarData = useSelector(
        (state: PortalState) => state.portal.sideBarData
    );
    const status: SurveyorCompletionStatus | undefined = useSelector(
        (state: PortalState) =>
            state.portal.activeInspection?.SurveyorCompletionStatus
    );
    const { setAnchor, setAllowDelete } = React.useContext(
        MainFrameContext
    ) as MainFrameContextType;
    const location = useLocation();
    const selectedColour = "#2D2859";
    const notSelectedColour = "#5C5F62";
    const baseInspectionUrl = "/inspection/" + data?.inspectionId;
    const inspectionLinkColour =
        location.pathname === baseInspectionUrl
            ? selectedColour
            : notSelectedColour;
    const history = useHistory();
    const isDocumentCollector: boolean = useSelector((state: any) =>  userIsDocCollector(state));
    const dispatch = useDispatch();

    const { experimentSurveyorDefectImageAssignment } = useFlags();

    useEffect(() => {
        return history.listen((location) => {

            dispatch(clearDeleteData());

            setAllowDelete(!location.pathname.endsWith("/photos"));

            if (
                history.action === "PUSH" &&
                location.pathname === `${baseInspectionUrl}/checklist`
            ) {
                setAnchor("#landing");
            }

            if (
                history.action === "PUSH" &&
                location.pathname === `${baseInspectionUrl}/documents`
            ) {
                setAnchor("#landing");
            }

            if (
                history.action === "PUSH" &&
                location.pathname === `${baseInspectionUrl}/photos`
            ) {
                setAnchor("#landing");
            }

            if (
                history.action === "POP" &&
                location.pathname.includes(`${baseInspectionUrl}/photos`)
            ) {
                setAnchor("#landing");
            }
        });
    }, [history, baseInspectionUrl, setAnchor, setAllowDelete]);

    const photoElements: any[] = [];

    if (location.pathname.includes("photos")) {
        photoElements.push({
            url: `${baseInspectionUrl}/photos`,
            label: "All Photos"
        });

        data?.imageElements.forEach((element) => {
            photoElements.push({
                url: `${baseInspectionUrl}/photos/${element.id}`,
                label: element.name
            });
        });
    }

    const getBadgeWithCount = (count: number, assetType: number) => {
        let displayTick = false;

        if (status !== undefined && status !== null && !isDocumentCollector) {
            if (assetType === AssetType.Document) {
                displayTick = status.DocumentsComplete;
            }
            if (assetType === AssetType.Image) {
                displayTick = status.PhotosComplete;
            }
            if (assetType === AssetType.CheckList) {
                displayTick = status.ChecklistComplete;
            }
        }

        return (
            <div>
                {displayTick ? (
                    <div className={style.BigTick}>
                        <Icon color="success" source={CircleTickMajor} />
                    </div>
                ) : (
                    <span className={style.count}>{count}</span>
                )}
            </div>
        );
    };

    const getTick = (assetType: number) => {
        let displayTick = false;

        if (status !== undefined && status !== null) {
            if (assetType === AssetType.CarbonOffsetting) {
                displayTick = status.CarbonSectionComplete;
            }
        }

        if (displayTick) {
            return (
                <div>
                    <div className={style.BigTick}>
                        <Icon color="success" source={CircleTickMajor} />
                    </div>
                </div>
            );
        } else {
            return null;
        }
    };

    // We only want to be able to use the back arrow if the user is not locked to the
    // current inspection (via an auto login token.)
    const backArrowTo: string = !Boolean(
        localStorage.getItem("InspectionViewOnly")
    ).valueOf() ? "/" : "#";

    const items = [];
    if (data) {
        if (data.numberOfChecklistElements > 0) {
            items.push({
                url: data
                    ? `${baseInspectionUrl}/checklist`
                    : "/Error/404",
                label: "Inspection Checklists",
                badge: getBadgeWithCount(
                    data.numberOfCheckLists,
                    AssetType.CheckList
                ),
                icon: ChecklistMajor
            });
        }

        if (data.numberOfDocumentElements > 0) {
            items.push({
                url: data
                    ? `${baseInspectionUrl}/documents`
                    : "/Error/404",
                label: "Documents",
                icon: NoteMajor,
                badge: getBadgeWithCount(
                    data.numberOfDocuments,
                    AssetType.Document
                )
            });
        }

        if (data.numberOfPhotoElements > 0) {
            items.push({
                url: data
                    ? `${baseInspectionUrl}/photos`
                    : "/Error/404",
                label: "Photos",
                icon: CameraMajor,
                subNavigationItems: photoElements,
                onClick() {
                    setAnchor("#landing");
                    setAllowDelete(false);
                },
                badge: getBadgeWithCount(
                    data.numberOfPhotos,
                    AssetType.Image
                )
            });
        }

        if (experimentSurveyorDefectImageAssignment) {
            items.push({
                url: data
                    ? `${baseInspectionUrl}/defects`
                    : "/Error/404",
                label: "Defects",
                icon: FolderMajor,
                badge: null
            });
        }

        if (!isDocumentCollector) {
            items.push({
                url: data
                    ? `${baseInspectionUrl}/carbon-offsetting`
                    : "/Error/404",
                label: "Carbon Offsetting",
                icon: NatureMajor,
                badge: getTick(AssetType.CarbonOffsetting)
            })
        }
    }

    return (
        <Navigation location={location.pathname}>
            <div
                className={style.SideBar}
                style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: "10px"
                }}
            >
                {
                    isDocumentCollector ?
                        <div className={style.SideBarTitleNoBackButton}/>
                    :
                        <Link
                            style={{ textDecoration: "none" }}
                            to={backArrowTo}
                        >
                        <div className={style.Arrow}>
                            <img
                                src={arrow}
                                id={"to_inspections_arrow"}
                                alt={"arrow"}
                            />
                        </div>
                        </Link>
                }
                <Link
                    style={{ textDecoration: "none" }}
                    to={baseInspectionUrl || ""}
                >
                    <div>
                        <p
                            className={style.SideBarTitle}
                            style={{ color: inspectionLinkColour }}
                        >
                            {data?.shipName || ""}
                        </p>
                        <p
                            className={style.SideBarSubTitle}
                            style={{ color: inspectionLinkColour }}
                        >
                            {data?.shipIMO || ""}
                        </p>
                    </div>
                </Link>
            </div>

            {data ? (
                <div>
                    <Navigation.Section
                        items={items}
                    />
                    {}
                </div>
            ) : null}
        </Navigation>
    );
}
